import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeftIcon,
  CloudUploadIcon,
  DeleteIcon,
  DocumentsIcon,
  EditIcon,
  ViewIcon,
  FaFileAlt,
  FaCloudUploadAlt,
  FaEye,
  FaEdit,
  SearchIcon,
  CloseIcon,
  UsersIcon,
} from "../../Icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Modal from "../../components/Modal";
import { displayDateUS, isDateExpired } from "../../utils/dateUtils";
import CredentialModal from "./components/CredentialModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import DocumentBulkActions from "./components/DocumentBulkActions";
import { useDocuments } from "./hooks/useDocuments";
import { useRowSelection } from "./hooks/useRowSelection";
import Table from "../../components/UI/Table";
import TaxFormModal from "./components/TaxFormModal";
import ServiceLog from "./components/ServiceLog";
import useCurrentUser from "../../hooks/useCurrentUser";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
  PAGE_ACTIONS,
  MODULE_TAB_BTN_SCROLL,
} from "../../ui/pageChrome";
import {
  DOCUMENT_THEME,
  DOCUMENT_ROLE_CHIP,
  DOCUMENT_BTN,
  DOCUMENT_COL,
  documentStatusClass,
  formatDocumentStatus,
} from "./documentTheme";
import toast from "react-hot-toast";
import {
  buildBulkPrintHtml,
  getDocumentRowKey,
  openDocumentsPrintTab,
} from "../../print/documentPrintService";
import {
  INACTIVE_EMPLOYEE_DOCUMENT_WRITE_MESSAGE,
  isEmployeeInactiveStatus,
} from "../../constants/employeeStatus";
import { BACKEND_FORM_KEY_TO_LIBRARY_ID } from "../Application/formLibraryFillStatus";
import {
  buildApplicationFormDocumentUrl,
  buildEvaluationDocumentUrl,
  canMutateHrDocuments,
  canOpenDocumentFormRow,
  canViewHrDocuments,
  getDocumentFormActionLabel,
  isSurveyorUser,
} from "../../utils/documentPermissions";
import {
  filterRowsForSurveyor,
  SURVEYOR_EMPTY_DOCUMENTS_MESSAGE,
} from "../../utils/surveyorDocumentRows";

/** Maps tenant role/position label to application route slug (rn, pt, …). Shared by edit + evaluation print. */
function getPositionSlugFromRoleName(roleName) {
  if (!roleName) return "rn";
  const lowerRole = roleName.toLowerCase();
  if (lowerRole.includes("registered nurse") || lowerRole === "rn")
    return "rn";
  if (
    lowerRole.includes("physical therapist assistant") ||
    lowerRole === "pta"
  )
    return "pta";
  if (lowerRole.includes("physical therapist") || lowerRole === "pt")
    return "pt";
  if (
    lowerRole.includes("occupational therapist assistant") ||
    lowerRole === "ota"
  )
    return "ota";
  if (lowerRole.includes("occupational therapist") || lowerRole === "ot")
    return "ot";
  if (lowerRole.includes("speech") || lowerRole === "st") return "st";
  if (lowerRole.includes("social worker") || lowerRole === "msw")
    return "msw";
  if (lowerRole.includes("home health aide") || lowerRole === "hha")
    return "hha";
  if (lowerRole.includes("vocational nurse") || lowerRole === "lvn")
    return "lvn";
  if (
    lowerRole.includes("certified nursing assistant") ||
    lowerRole === "cna"
  )
    return "cna";
  if (
    lowerRole.includes("caregiver") ||
    lowerRole === "cg" ||
    lowerRole === "care giver"
  )
    return "caregiver";
  if (
    lowerRole.includes("office assistant") ||
    lowerRole === "office-assistant" ||
    lowerRole === "oa"
  )
    return "oa";
  if (lowerRole.includes("contractor") || lowerRole === "con") return "con";
  if (lowerRole.includes("admin") || lowerRole === "admin") return "admin";
  return "rn";
}

const Documents = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [currentCredential, setCurrentCredential] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [currentHistoryRow, setCurrentHistoryRow] = useState(null);
  const [isTaxFormModalOpen, setIsTaxFormModalOpen] = useState(false);
  const [currentTaxForm, setCurrentTaxForm] = useState(null);
  const [itemToDeleteType, setItemToDeleteType] = useState(null); // 'credential' or 'tax'
  const [bulkPrintLoading, setBulkPrintLoading] = useState(false);
  const { isAdminRole, isSurveyRole, tenantId, companyName, officeLogo, seniorStaffNurse, user: sessionUser } = useCurrentUser();
  const surveyorDocumentsView = isSurveyRole || isSurveyorUser(sessionUser);

  const {
    viewMode,
    isAdmin,
    activeTab,
    setActiveTab,
    selectedUser,
    visibleDocumentTabs,
    displayedUsersList,
    userSortKey,
    userSortOrder,
    toggleUserSort,
    loadingUsers,
    userPage,
    setUserPage,
    totalUserPages,
    applicationForms,
    loadingAppForms,
    appFormsPage,
    setAppFormsPage,
    totalAppFormsPages,
    credentials,
    loadingCredentials,
    credentialsPage,
    setCredentialsPage,
    totalCredentialsPages,
    medicalForms,
    loadingMedicalForms,
    medicalFormsPage,
    setMedicalFormsPage,
    totalMedicalFormsPages,
    taxForms,
    loadingTaxForms,
    taxFormsPage,
    setTaxFormsPage,
    totalTaxFormsPages,
    evaluationForms,
    loadingEvaluationForms,
    evaluationFormsPage,
    setEvaluationFormsPage,
    totalEvaluationFormsPages,
    totalUsers,
    userLimit,
    appFormsTotal,
    appFormsLimit,
    credentialsTotal,
    credentialsLimit,
    medicalFormsTotal,
    medicalFormsLimit,
    taxFormsTotal,
    taxFormsLimit,
    evaluationFormsTotal,
    evaluationFormsLimit,
    saveCredential,
    deleteCredential,
    saveTaxForm,
    deleteTaxForm,
    deleteEvaluation,
    deleteDocument,
    getCredentialFileUrl,
    getTaxFileUrl,
    getCredential,
    getTaxFormById,
    searchTerm,
    setSearchTerm,
  } = useDocuments();

  const isEmployeeInactive = isEmployeeInactiveStatus(selectedUser?.status);
  /** Inactive employee docs: Admin/OA get read + print only (no edit/delete/upload). */
  const canMutateDocuments = canMutateHrDocuments({
    isEmployeeInactive,
    isSurveyRole,
  });
  const canViewDocuments = canViewHrDocuments({ isEmployeeInactive });
  const canDeleteDocuments = isAdminRole && !isEmployeeInactive;

  const handleViewFile = async (e, id, type) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      let response;
      if (type === "credential") {
        response = await getCredentialFileUrl(id);
      } else {
        response = await getTaxFileUrl(id);
      }

      if (response && response.status === 1 && response.result?.url) {
        window.open(response.result.url, "_blank");
      }
    } catch (error) {
      console.error("Error generating file URL:", error);
    }
  };

  const openApplicationFormFromRow = (row) => {
    if (!selectedUser) return;
    if (!canViewDocuments) {
      toast.error(INACTIVE_EMPLOYEE_DOCUMENT_WRITE_MESSAGE);
      return;
    }

    const actualUserId = selectedUser.id;

    if (activeTab === "credentials") {
      const details = row.details || [];
      const latest =
        details.length > 0
          ? details.reduce((prev, current) =>
            new Date(prev.createdAt) > new Date(current.createdAt)
              ? prev
              : current,
          )
          : null;

      if (!latest?.id) {
        toast.error("No credential document on file yet.");
        return;
      }

      setModalMode(isSurveyRole ? "view" : "edit");
      setCurrentCredential({ ...row, targetDetailId: latest.id });
      setIsModalOpen(true);
      return;
    }

    const positionId = getPositionSlugFromRoleName(
      selectedUser.role?.name || selectedUser.position,
    );

    const formId =
      BACKEND_FORM_KEY_TO_LIBRARY_ID[row.formKey] ||
      row.formKey ||
      row.code ||
      row.id;

    const isEvaluationTab =
      activeTab === "evaluation-forms" ||
      row.formKey === "evaluation" ||
      !!row.detail?.reviewer;

    const returnUrl = `${location.pathname}${location.search}`;

    if (isEvaluationTab) {
      const role = row.type?.toLowerCase() || positionId;
      navigate(
        buildEvaluationDocumentUrl({
          role,
          evalId: row.detail?.id,
          userId: actualUserId,
          tab: activeTab,
          returnUrl,
        }),
      );
      return;
    }

    navigate(
      buildApplicationFormDocumentUrl({
        positionId,
        userId: actualUserId,
        formId,
        recordId: row.detail?.id,
        tab: activeTab,
        returnUrl,
      }),
    );
  };

  const handleEditDocument = (row) => {
    if (!canMutateDocuments) return;
    openApplicationFormFromRow(row);
  };

  const handleViewDocument = (row) => {
    if (!canViewDocuments) return;
    openApplicationFormFromRow(row);
  };

  const handleTaxFormEdit = (row) => {
    if (!selectedUser) return;
    if (isEmployeeInactive) {
      toast.error(INACTIVE_EMPLOYEE_DOCUMENT_WRITE_MESSAGE);
      return;
    }

    const details = row.details || [];
    const latest =
      details.length > 0
        ? details.reduce((prev, current) =>
          new Date(prev.createdAt) > new Date(current.createdAt)
            ? prev
            : current,
        )
        : null;

    setModalMode("edit");
    setCurrentTaxForm({ ...row, targetDetailId: latest?.id });
    setIsTaxFormModalOpen(true);
  };

  const handleTaxFormUploadClick = (row) => {
    if (isEmployeeInactive) {
      toast.error(INACTIVE_EMPLOYEE_DOCUMENT_WRITE_MESSAGE);
      return;
    }
    setModalMode("create");
    setCurrentTaxForm(row);
    setIsTaxFormModalOpen(true);
  };

  const handleSaveTaxForm = async (formData) => {
    const taxFormId =
      modalMode === "edit"
        ? currentTaxForm.targetDetailId || currentTaxForm.id
        : null;
    const result = await saveTaxForm(
      {
        ...formData,
        type: currentTaxForm?.formKey || currentTaxForm?.type || "w_4",
      },
      modalMode,
      taxFormId,
    );
    return result;
  };

  const handleDeleteTaxForm = async (row, detailId = null) => {
    if (isEmployeeInactive) {
      toast.error(INACTIVE_EMPLOYEE_DOCUMENT_WRITE_MESSAGE);
      return;
    }
    let targetId;
    let displayName;

    if (detailId) {
      const detail = (row.details || []).find((d) => d.id === detailId);
      targetId = detailId;
      displayName = detail?.displayName || row.name;
    } else {
      const details = row.details || [];
      const latest =
        details.length > 0
          ? details.reduce((prev, current) =>
            new Date(prev.createdAt) > new Date(current.createdAt)
              ? prev
              : current,
          )
          : null;
      targetId = latest?.id || row.id;
      displayName = latest?.displayName || row.name;
    }

    setItemToDelete({
      id: targetId,
      rowName: row.name,
      displayName: displayName,
    });
    setItemToDeleteType("tax");
    setIsDeleteModalOpen(true);
  };

  const handleUploadClick = (row) => {
    if (isEmployeeInactive) {
      toast.error(INACTIVE_EMPLOYEE_DOCUMENT_WRITE_MESSAGE);
      return;
    }
    // Open modal in 'create' mode (to add new history/renew)
    // but pre-fill Display Name and Type from the row.
    setModalMode("create");
    setCurrentCredential(row);
    setIsModalOpen(true);
  };

  const handleSaveCredential = async (formData) => {
    // If editing, we might need the ID from currentCredential
    // But the backend uses 'type' for create? The user postman shows /api/credentials (POST) with type, and /api/credentials/1 (PUT).
    // If we are 'editing' an existing row, we use its ID.
    // If we are 'creating' a new one, we need to decide the type.
    // For now, let's use the type from the row or a default if creating from scratch.

    const credentialId =
      modalMode === "edit"
        ? currentCredential.targetDetailId || currentCredential.id
        : null;

    const result = await saveCredential(
      {
        ...formData,
        type:
          currentCredential?.formKey ||
          currentCredential?.type ||
          "professional_license",
      },
      modalMode,
      credentialId,
    );
    return result;
  };

  const handleDeleteCredential = async (row, detailId = null) => {
    if (isEmployeeInactive) {
      toast.error(INACTIVE_EMPLOYEE_DOCUMENT_WRITE_MESSAGE);
      return;
    }
    let targetId;
    let displayName;

    if (detailId) {
      const detail = (row.details || []).find((d) => d.id === detailId);
      targetId = detailId;
      displayName = detail?.displayName || row.name;
    } else {
      const details = row.details || [];
      const latest =
        details.length > 0
          ? details.reduce((prev, current) =>
            new Date(prev.createdAt) > new Date(current.createdAt)
              ? prev
              : current,
          )
          : null;
      targetId = latest?.id || row.id;
      displayName = latest?.displayName || row.name;
    }

    setItemToDelete({
      id: targetId,
      rowName: row.name,
      displayName: displayName,
    });
    setItemToDeleteType("credential");
    setIsDeleteModalOpen(true);
  };

  const handleDeleteEvaluation = (row) => {
    if (isEmployeeInactive) {
      toast.error(INACTIVE_EMPLOYEE_DOCUMENT_WRITE_MESSAGE);
      return;
    }
    const roleType = row.type || selectedUser?.role?.name || selectedUser?.position || "RN";
    setItemToDelete({
      id: row.detail?.id || row.id,
      type: roleType,
      employee: row.employee,
      rowName: row.name || "Evaluation",
    });
    setItemToDeleteType("evaluation");
    setIsDeleteModalOpen(true);
  };

  const handleDeleteDocument = (row) => {
    if (isEmployeeInactive) {
      toast.error(INACTIVE_EMPLOYEE_DOCUMENT_WRITE_MESSAGE);
      return;
    }
    setItemToDelete({
      id: row.detail?.id,
      rowName: row.name,
      formKey: row.formKey
    });
    setItemToDeleteType("document");
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    setIsDeleting(true);
    try {
      if (itemToDeleteType === "credential") {
        await deleteCredential(itemToDelete.id);
      } else if (itemToDeleteType === "evaluation") {
        await deleteEvaluation(itemToDelete.id, itemToDelete.type);
      } else if (itemToDeleteType === "document") {
        await deleteDocument(itemToDelete.id, itemToDelete.formKey, activeTab);
      } else {
        await deleteTaxForm(itemToDelete.id);
      }
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      setItemToDeleteType(null);
      // Close the history modal as well after a successful delete
      setIsHistoryModalOpen(false);
      setCurrentHistoryRow(null);
    } catch (error) {
      console.error("Delete failed", error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Column Definitions for Application / Medical forms
  const DEFAULT_PRINT_LOGO = "/login-logo.png";

  const getPrintLogoAbsoluteUrl = () => {
    const logo = officeLogo || "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    if (!logo || !String(logo).trim()) {
      return `${origin}${DEFAULT_PRINT_LOGO}`;
    }
    if (logo.startsWith("http")) return logo;
    const normalized = logo.startsWith("/") ? logo : `/${logo}`;
    return `${origin}${normalized}`;
  };

  const getPrintBranding = () => ({
    companyName: companyName || "Company",
    logoUrl: getPrintLogoAbsoluteUrl(),
    seniorStaffNurse: seniorStaffNurse || "",
  });

  const applicationFormsData = useMemo(
    () =>
      filterRowsForSurveyor(
        (applicationForms || []).map((row, index) => ({
          ...row,
          _rowKey:
            row._id ??
            row.detail?.id ??
            row.formKey ??
            row.id ??
            `form-${index}`,
        })),
        "form",
        surveyorDocumentsView,
      ),
    [applicationForms, surveyorDocumentsView],
  );

  const medicalFormsData = useMemo(
    () =>
      filterRowsForSurveyor(
        (medicalForms || []).map((row, index) => ({
          ...row,
          _rowKey:
            row._id ??
            row.detail?.id ??
            row.formKey ??
            row.id ??
            `form-${index}`,
        })),
        "form",
        surveyorDocumentsView,
      ),
    [medicalForms, surveyorDocumentsView],
  );

  const evaluationFormsData = useMemo(
    () =>
      filterRowsForSurveyor(
        (evaluationForms || []).map((row, index) => ({
          ...row,
          _rowKey:
            row.detail?.id || row.id || `${row.type || "eval"}-${index}`,
        })),
        "form",
        surveyorDocumentsView,
      ),
    [evaluationForms, surveyorDocumentsView],
  );

  const taxFormsData = useMemo(
    () => filterRowsForSurveyor(taxForms, "upload", surveyorDocumentsView),
    [taxForms, surveyorDocumentsView],
  );

  const credentialsData = useMemo(
    () => filterRowsForSurveyor(credentials, "upload", surveyorDocumentsView),
    [credentials, surveyorDocumentsView],
  );

  const documentPagination = (rows, page) =>
    surveyorDocumentsView
      ? {
          currentPage: 1,
          totalPages: 1,
          onPageChange: () => {},
          totalItems: rows.length,
          itemsPerPage: Math.max(rows.length, 1),
        }
      : page;

  const documentEmptyMessage = surveyorDocumentsView
    ? SURVEYOR_EMPTY_DOCUMENTS_MESSAGE
    : undefined;

  const appSelection = useRowSelection(
    applicationFormsData,
    getDocumentRowKey,
  );
  const medicalSelection = useRowSelection(
    medicalFormsData,
    getDocumentRowKey,
  );
  const evaluationSelection = useRowSelection(
    evaluationFormsData,
    getDocumentRowKey,
  );

  useEffect(() => {
    appSelection.clearSelection();
    medicalSelection.clearSelection();
    evaluationSelection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedUser?.id]);

  const runBulkPrint = async (rows, kind, documentTitle) => {
    if (!selectedUser) {
      toast.error("No employee selected.");
      return;
    }
    if (!rows?.length) {
      toast.error("Select at least one document to print.");
      return;
    }
    const employeeName =
      `${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim();
    setBulkPrintLoading(true);
    const toastId = toast.loading(
      rows.length === 1
        ? "Preparing document for print…"
        : `Preparing ${rows.length} documents for print…`,
    );
    try {
      const { documents, printed, failed } = await buildBulkPrintHtml(rows, {
        kind,
        branding: getPrintBranding(),
        selectedUserId: selectedUser.id,
        sessionUserId: sessionUser?.id,
        isAdminRole,
        positionSlug: getPositionSlugFromRoleName(
          selectedUser?.role?.name || selectedUser?.position,
        ),
        documentTitle,
        employeeName,
      });
      if (!printed || !documents?.length) {
        toast.error(
          failed[0]?.reason || "No printable documents in selection.",
          { id: toastId },
        );
        return;
      }
      const opened = openDocumentsPrintTab(documents, {
        title:
          documentTitle ||
          `Print ${printed} form${printed === 1 ? "" : "s"}`,
        subtitle: `${employeeName || "Employee"}${
          selectedUser.email ? ` · ${selectedUser.email}` : ""
        }`,
      });
      if (!opened) {
        toast.error("Pop-up blocked. Allow pop-ups to print.", { id: toastId });
        return;
      }
      if (failed.length) {
        toast.success(
          `Opened print for ${printed} document(s). ${failed.length} skipped.`,
          { id: toastId },
        );
      } else {
        toast.success(
          printed === 1
            ? "Print preview opened. Use Print in the new tab."
            : `Print preview opened for ${printed} documents. Use Print in the new tab.`,
          { id: toastId },
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to prepare print.", { id: toastId });
    } finally {
      setBulkPrintLoading(false);
    }
  };

  const buildTableSelection = (selection) => ({
    enabled: true,
    isSelected: (row, index) =>
      selection.isSelected(getDocumentRowKey(row, index)),
    isSelectable: () => true,
    onToggleRow: (row, index, checked) =>
      selection.toggleRow(getDocumentRowKey(row, index), checked),
    allSelected: selection.allSelectableSelected,
    someSelected: selection.someSelectableSelected,
    onToggleAll: selection.toggleAllSelectable,
  });

  // Column Definitions for Documents
  const documentColumns = [
    {
      header: "Name",
      accessor: "name",
      className: DOCUMENT_COL.name,
      render: (row) => (
        <span
          className="block text-[13px] font-medium text-slate-900 truncate"
          title={row.name || ""}
        >
          {row.name || "—"}
        </span>
      ),
    },
    {
      header: "Status",
      className: DOCUMENT_COL.status,
      render: (row) => {
        const status = row.detail?.status || row.status || "pending";
        return (
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold ${documentStatusClass(status)}`}
          >
            {formatDocumentStatus(status)}
          </span>
        );
      },
    },
    {
      header: "Created At",
      className: DOCUMENT_COL.date,
      render: (row) => {
        const date = row.detail?.createdAt;
        return (
          <span className="text-[13px] text-slate-600 tabular-nums">
            {date ? displayDateUS(date) : "—"}
          </span>
        );
      },
    },
    {
      header: "Actions",
      className: DOCUMENT_COL.actions,
      render: (row) => {
        const hasDetail = !!row.detail;
        const canOpen = canOpenDocumentFormRow({ isSurveyRole, hasDetail });
        const actionLabel = getDocumentFormActionLabel({
          isSurveyRole,
          canMutate: canMutateDocuments,
          hasDetail,
        });
        return (
          <div className="inline-flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!canOpen) return;
                if (isSurveyRole) {
                  handleViewDocument(row);
                } else if (canMutateDocuments && hasDetail) {
                  handleEditDocument(row);
                }
              }}
              disabled={!canOpen || (!isSurveyRole && !canMutateDocuments)}
              className={DOCUMENT_BTN.soft}
              title={
                isEmployeeInactive
                  ? "Employee inactive — read only"
                  : !canOpen
                    ? "Not Started"
                    : actionLabel
              }
            >
              {isSurveyRole ? <ViewIcon size={13} /> : <EditIcon size={13} />}
              {actionLabel}
            </button>
            {canDeleteDocuments && hasDetail && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteDocument(row);
                }}
                className={DOCUMENT_BTN.danger}
                title="Delete Document"
              >
                <DeleteIcon size={13} />
                Delete
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const credentialColumns = [
    {
      header: "Name",
      className: DOCUMENT_COL.nameDense,
      render: (row) => (
        <span
          className="block text-[13px] font-medium text-slate-900 truncate"
          title={row.name || ""}
        >
          {row.name || "—"}
        </span>
      ),
    },
    {
      header: "Expiry Date",
      className: DOCUMENT_COL.date,
      render: (row) => {
        const details = row.details || [];
        if (details.length === 0)
          return (
            <span className="text-[13px] text-slate-400 italic">Pending</span>
          );

        const latest = details.reduce((prev, current) => {
          return new Date(prev.createdAt) > new Date(current.createdAt)
            ? prev
            : current;
        });

        const isExpired = isDateExpired(latest.expireAt);

        return (
          <span
            className={`text-[13px] tabular-nums ${isExpired ? "text-red-600 font-medium" : "text-slate-700"}`}
          >
            {displayDateUS(latest.expireAt)}
            {isExpired ? (
              <span className="block text-[10px] uppercase tracking-wide">
                Expired
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      header: "Note",
      className: DOCUMENT_COL.note,
      render: (row) => {
        const details = row.details || [];
        if (details.length === 0)
          return <span className="text-[13px] text-slate-400">—</span>;

        const latest = details.reduce((prev, current) => {
          return new Date(prev.createdAt) > new Date(current.createdAt)
            ? prev
            : current;
        });

        return (
          <span
            className="block text-[13px] text-slate-600 truncate"
            title={latest.notes || ""}
          >
            {latest.notes || "—"}
          </span>
        );
      },
    },
    {
      header: "Upload",
      className: DOCUMENT_COL.upload,
      render: (row) => {
        const details = row.details || [];
        const latest =
          details.length > 0
            ? details.reduce((prev, current) => {
                return new Date(prev.createdAt) > new Date(current.createdAt)
                  ? prev
                  : current;
              })
            : null;

        return (
          <div className="flex flex-col gap-1 items-start">
            {latest ? (
              <button
                type="button"
                onClick={(e) => handleViewFile(e, latest.id, "credential")}
                className={DOCUMENT_BTN.link}
              >
                <DocumentsIcon size={13} />
                View Latest
              </button>
            ) : (
              <span className="text-slate-400 italic text-[11px]">
                No document
              </span>
            )}
            {canMutateDocuments && (
              <button
                type="button"
                onClick={() => handleUploadClick(row)}
                className={DOCUMENT_BTN.outline}
              >
                <CloudUploadIcon size={13} />
                Upload New
              </button>
            )}
          </div>
        );
      },
    },
    {
      header: "Actions",
      className: DOCUMENT_COL.actions,
      render: (row) => (
        <div className="inline-flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCurrentHistoryRow(row);
              setIsHistoryModalOpen(true);
            }}
            className={DOCUMENT_BTN.soft}
            title="View History"
          >
            <ViewIcon size={13} />
            View
          </button>
          {canMutateDocuments && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEditDocument(row);
              }}
              disabled={!row.details || row.details.length === 0}
              className={DOCUMENT_BTN.soft}
              title={
                !row.details || row.details.length === 0
                  ? "No document to edit"
                  : isEmployeeInactive
                    ? "Employee inactive — read only"
                    : "Edit"
              }
            >
              <EditIcon size={13} />
              Edit
            </button>
          )}
          {isSurveyRole && row.details?.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDocument(row);
              }}
              className={DOCUMENT_BTN.soft}
              title="View credential"
            >
              <FaEye size={13} />
              Open
            </button>
          )}
        </div>
      ),
    },
  ];

  const taxFormColumns = [
    {
      header: "Form Type",
      className: DOCUMENT_COL.nameDense,
      render: (row) => (
        <span
          className="block text-[13px] font-medium text-slate-900 truncate"
          title={row.name || ""}
        >
          {row.name || "—"}
        </span>
      ),
    },
    {
      header: "Upload",
      className: DOCUMENT_COL.upload,
      render: (row) => {
        const details = row.details || [];
        const latest =
          details.length > 0
            ? details.reduce((prev, current) => {
                return new Date(prev.createdAt) > new Date(current.createdAt)
                  ? prev
                  : current;
              })
            : null;

        return (
          <div className="flex flex-col gap-1 items-start">
            {latest ? (
              <button
                type="button"
                onClick={(e) => handleViewFile(e, latest.id, "tax")}
                className={DOCUMENT_BTN.link}
              >
                <FaFileAlt size={13} />
                View Latest
              </button>
            ) : (
              <span className="text-slate-400 italic text-[11px]">
                No document
              </span>
            )}

            {canMutateDocuments && (
              <button
                type="button"
                onClick={() => handleTaxFormUploadClick(row)}
                className={DOCUMENT_BTN.outline}
              >
                <FaCloudUploadAlt size={13} />
                Upload New
              </button>
            )}
          </div>
        );
      },
    },
    {
      header: "Notes",
      className: DOCUMENT_COL.note,
      render: (row) => {
        const details = row.details || [];
        if (details.length === 0)
          return <span className="text-[13px] text-slate-400">—</span>;

        const latest = details.reduce((prev, current) => {
          return new Date(prev.createdAt) > new Date(current.createdAt)
            ? prev
            : current;
        });

        const noteText = latest.comments || latest.notes || "";
        return (
          <span
            className="block text-[13px] text-slate-600 truncate"
            title={noteText}
          >
            {noteText || "—"}
          </span>
        );
      },
    },
    {
      header: "Created At",
      className: DOCUMENT_COL.date,
      render: (row) => {
        const details = row.details || [];
        if (details.length === 0)
          return <span className="text-[13px] text-slate-400">—</span>;

        const latest = details.reduce((prev, current) => {
          return new Date(prev.createdAt) > new Date(current.createdAt)
            ? prev
            : current;
        });

        return (
          <span className="text-[13px] text-slate-600 tabular-nums">
            {displayDateUS(latest.createdAt)}
          </span>
        );
      },
    },
    {
      header: "Actions",
      className: DOCUMENT_COL.actions,
      render: (row) => (
        <div className="inline-flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCurrentHistoryRow(row);
              setIsHistoryModalOpen(true);
            }}
            className={DOCUMENT_BTN.soft}
            title="View History"
          >
            <FaEye size={13} />
            View
          </button>
          {canMutateDocuments && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleTaxFormEdit(row);
              }}
              disabled={!row.details || row.details.length === 0}
              className={DOCUMENT_BTN.soft}
              title={
                !row.details || row.details.length === 0
                  ? "No document to edit"
                  : isEmployeeInactive
                    ? "Employee inactive — read only"
                    : "Edit"
              }
            >
              <FaEdit size={13} />
              Edit
            </button>
          )}
        </div>
      ),
    },
  ];

  const evaluationColumns = [
    {
      header: "Name",
      className: DOCUMENT_COL.name,
      render: (row) => (
        <span
          className="block text-[13px] font-medium text-slate-900 truncate"
          title={row.name || ""}
        >
          {row.name || "—"}
        </span>
      ),
    },
    {
      header: "Status",
      className: DOCUMENT_COL.status,
      render: (row) => {
        const status = row.detail?.status || row.status || "pending";
        return (
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold ${documentStatusClass(status)}`}
          >
            {formatDocumentStatus(status)}
          </span>
        );
      },
    },
    {
      header: "Reviewer",
      className: DOCUMENT_COL.note,
      render: (row) => (
        <span className="block text-[13px] text-slate-700 truncate">
          {row?.detail?.reviewer
            ? `${row.detail.reviewer.firstName || ""} ${row.detail.reviewer.lastName || ""}`.trim() ||
              "—"
            : "—"}
        </span>
      ),
    },
    {
      header: "Eval Date",
      className: DOCUMENT_COL.date,
      render: (row) => {
        const dateStr = row?.detail?.evaluationDate || row?.evaluationDate;
        return (
          <span className="text-[13px] text-slate-600 tabular-nums">
            {dateStr ? displayDateUS(dateStr) || "—" : "—"}
          </span>
        );
      },
    },
    {
      header: "Actions",
      className: DOCUMENT_COL.actions,
      render: (row) => {
        const hasDetail = !!row.detail;
        const canOpen = canOpenDocumentFormRow({ isSurveyRole, hasDetail });
        const actionLabel = getDocumentFormActionLabel({
          isSurveyRole,
          canMutate: canMutateDocuments,
          hasDetail,
        });
        return (
          <div className="inline-flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (!canOpen) return;
                if (isSurveyRole) {
                  handleViewDocument(row);
                } else if (canMutateDocuments && hasDetail) {
                  handleEditDocument(row);
                }
              }}
              disabled={!canOpen || (!isSurveyRole && !canMutateDocuments)}
              className={DOCUMENT_BTN.soft}
              title={
                isEmployeeInactive
                  ? "Employee inactive — read only"
                  : !canOpen
                    ? "Not Started"
                    : actionLabel
              }
            >
              {isSurveyRole ? <ViewIcon size={13} /> : <EditIcon size={13} />}
              {actionLabel}
            </button>
            {canDeleteDocuments && hasDetail && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteEvaluation(row);
                }}
                className={DOCUMENT_BTN.danger}
                title="Delete Evaluation"
              >
                <DeleteIcon size={13} />
                Delete
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const userColumns = [
    {
      header: "Name",
      accessor: "firstName",
      sortable: true,
      className: DOCUMENT_COL.empName,
      render: (user) => (
        <span className="block text-[13px] font-medium text-slate-900 truncate">
          {user.firstName} {user.lastName}
        </span>
      ),
    },
    {
      header: "Email",
      accessor: "email",
      sortable: true,
      className: DOCUMENT_COL.empEmail,
      render: (user) => (
        <span
          className="block text-[13px] text-slate-600 truncate"
          title={user.email || ""}
        >
          {user.email || "—"}
        </span>
      ),
    },
    {
      header: "Role",
      accessor: "roleName",
      sortable: true,
      className: DOCUMENT_COL.empRole,
      render: (user) => (
        <span
          className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wide ${DOCUMENT_ROLE_CHIP}`}
        >
          {user.role?.name || "N/A"}
        </span>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      sortable: true,
      className: DOCUMENT_COL.empStatus,
      render: (user) => (
        <span
          className={`inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold ${documentStatusClass(user.status)}`}
        >
          {user.status || "—"}
        </span>
      ),
    },
    {
      header: "Actions",
      className: DOCUMENT_COL.empActions,
      render: (user) => (
        <div className="inline-flex items-center justify-end">
          <button
            type="button"
            onClick={() => navigate(`/documents/${user.id}`)}
            className={DOCUMENT_BTN.soft}
            title="View documents"
          >
            <ViewIcon size={13} />
            View
          </button>
        </div>
      ),
    },
  ];

  const historyColumns = [
    {
      header: "Document Name",
      className: DOCUMENT_COL.histName,
      render: (row) => (
        <span className="block text-[13px] font-medium text-slate-900 truncate">
          {row.displayName || "Document"}
        </span>
      ),
    },
    {
      header: "Submitted",
      className: DOCUMENT_COL.histDate,
      render: (row) => (
        <span className="text-[13px] text-slate-600 tabular-nums">
          {row.createdAt ? displayDateUS(row.createdAt) : "—"}
        </span>
      ),
    },
    {
      header: "Expiry",
      className: DOCUMENT_COL.histDate,
      render: (row) => {
        if (!row.expireAt)
          return <span className="text-[13px] text-slate-400 italic">No expiry</span>;
        const isExpired = isDateExpired(row.expireAt);
        return (
          <span
            className={`text-[13px] tabular-nums ${isExpired ? "text-red-600 font-medium" : "text-slate-700"}`}
          >
            {displayDateUS(row.expireAt)}
            {isExpired ? (
              <span className="block text-[10px] uppercase tracking-wide">Expired</span>
            ) : null}
          </span>
        );
      },
    },
    {
      header: "Actions",
      className: DOCUMENT_COL.histActions,
      render: (row) => (
        <div className="inline-flex items-center justify-end gap-1.5 flex-nowrap">
          {row.id && (
            <button
              type="button"
              onClick={(e) =>
                handleViewFile(
                  e,
                  row.id,
                  activeTab === "credentials" ? "credential" : "tax",
                )
              }
              className={DOCUMENT_BTN.soft}
              title="View Document"
            >
              <ViewIcon size={13} />
              View
            </button>
          )}
          {canDeleteDocuments && (
            <button
              type="button"
              onClick={() => {
                if (activeTab === "credentials") {
                  handleDeleteCredential(currentHistoryRow, row.id);
                } else {
                  handleDeleteTaxForm(currentHistoryRow, row.id);
                }
              }}
              className={DOCUMENT_BTN.danger}
              title="Delete Entry"
            >
              <DeleteIcon size={13} />
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  const taxHistoryColumns = [
    {
      header: "Form Type",
      className: DOCUMENT_COL.histForm,
      render: (row) => {
        const label = (
          row.type ||
          currentHistoryRow?.formKey ||
          currentHistoryRow?.name ||
          "—"
        )
          .replaceAll("_", " ")
          .toUpperCase();
        return (
          <span className="block text-[13px] font-medium text-slate-900 truncate">
            {label}
          </span>
        );
      },
    },
    {
      header: "Notes",
      className: DOCUMENT_COL.histNote,
      render: (row) => (
        <span
          className="block text-[13px] text-slate-600 truncate"
          title={row.comments || row.notes || ""}
        >
          {row.comments || row.notes || "—"}
        </span>
      ),
    },
    {
      header: "Created At",
      className: DOCUMENT_COL.histDate,
      render: (row) => (
        <span className="text-[13px] text-slate-600 tabular-nums">
          {row.createdAt ? displayDateUS(row.createdAt) : "—"}
        </span>
      ),
    },
    {
      header: "Actions",
      className: DOCUMENT_COL.histActions,
      render: (row) => (
        <div className="inline-flex items-center justify-end gap-1.5 flex-nowrap">
          {row.id && (
            <button
              type="button"
              onClick={(e) => handleViewFile(e, row.id, "tax")}
              className={DOCUMENT_BTN.soft}
              title="View Document"
            >
              <ViewIcon size={13} />
              View
            </button>
          )}
          {canDeleteDocuments && (
            <button
              type="button"
              onClick={() => handleDeleteTaxForm(currentHistoryRow, row.id)}
              className={DOCUMENT_BTN.danger}
              title="Delete Entry"
            >
              <DeleteIcon size={13} />
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];


  if (viewMode === "user-list") {
    return (
      <div className={PAGE_SHELL}>
        <div className={PAGE_HEADER_ROW}>
          <div>
            <h1 className={`text-2xl font-bold ${DOCUMENT_THEME.section.title}`}>
              HR Documents
            </h1>
            <p className={PAGE_SUBTITLE}>
              Search and select an employee to view their HR documents.
            </p>
          </div>
        </div>

        <div
          className={`bg-white rounded-xl border shadow-sm overflow-hidden ${DOCUMENT_THEME.section.wrap}`}
        >
          <div
            className={`flex items-center gap-2 h-11 px-4 border-b box-border ${DOCUMENT_THEME.section.head}`}
          >
            <span
              className={`w-0.5 h-4 rounded-full shrink-0 ${DOCUMENT_THEME.section.bar}`}
              aria-hidden
            />
            <span className={`shrink-0 ${DOCUMENT_THEME.section.icon}`}>
              <UsersIcon size={14} />
            </span>
            <h2
              className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${DOCUMENT_THEME.section.title}`}
            >
              Employee List
            </h2>
            {typeof totalUsers === "number" ? (
              <span
                className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${DOCUMENT_THEME.section.badge}`}
              >
                {totalUsers}
              </span>
            ) : null}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
            <div className="relative flex-1 min-w-0">
              <SearchIcon
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search employees..."
                className={`w-full h-8 pl-9 pr-8 text-sm bg-white border border-indigo-200 rounded-md text-slate-800 placeholder:text-slate-400 outline-none transition-colors ${DOCUMENT_THEME.fieldFocus}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                  aria-label="Clear search"
                >
                  <CloseIcon size={14} />
                </button>
              ) : null}
            </div>
          </div>

          <Table
            columns={userColumns}
            data={displayedUsersList}
            keyField="id"
            sortKey={userSortKey}
            sortOrder={userSortOrder}
            onSort={toggleUserSort}
            loading={loadingUsers}
            density="compact"
            embedded
            tableClassName="table-fixed w-full"
            srNoClassName={DOCUMENT_COL.srNo}
            headTone={DOCUMENT_THEME.tableHead}
            pagination={{
              currentPage: userPage,
              totalPages: totalUserPages,
              onPageChange: setUserPage,
              totalItems: totalUsers,
              itemsPerPage: userLimit,
            }}
            noDataMessage="No employees found."
          />
        </div>
      </div>
    );
  }

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          {selectedUser && (
            <div>
              <h1 className={`text-2xl font-bold ${DOCUMENT_THEME.section.title}`}>
                {selectedUser.firstName} {selectedUser.lastName}
              </h1>
              {isEmployeeInactive && (
                <span className="ml-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                  Inactive
                </span>
              )}
              <span className="text-gray-400">•</span>
              <span>{selectedUser.email}</span>
            </div>
          )}
        </div>
        {id && (
          <div className={PAGE_ACTIONS}>
            <button
              type="button"
              onClick={() => navigate("/documents")}
              className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border transition-colors ${DOCUMENT_THEME.button.outline}`}
            >
              <ArrowLeftIcon size={16} />
              Back
            </button>
          </div>
        )}
      </div>

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${DOCUMENT_THEME.section.wrap}`}
      >
        <nav
          className={`flex w-full overflow-x-auto border-b ${DOCUMENT_THEME.section.head}`}
          aria-label="Document tabs"
        >
          {visibleDocumentTabs.map((tab) => {
            const active = activeTab === tab;
            const label =
              tab === "service-log" ? "Service Log" : tab.replace("-", " ");
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`${MODULE_TAB_BTN_SCROLL} border-indigo-100 ${
                  active
                    ? DOCUMENT_THEME.tab.active
                    : DOCUMENT_THEME.tab.inactive
                }`}
              >
                <span className="truncate">{label}</span>
                {active ? (
                  <span
                    className={`absolute left-0 right-0 bottom-0 h-0.5 ${DOCUMENT_THEME.tab.underline}`}
                  />
                ) : null}
              </button>
            );
          })}
        </nav>

        <div className="bg-slate-50/50 p-3 sm:p-4 space-y-3">
          {isEmployeeInactive && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              This employee is <strong>Inactive</strong>. Documents are
              read-only — edit and delete are disabled. Print is still
              available. Contact customer support to reactivate.
            </div>
          )}

          {activeTab === "application-forms" && (
            <div
              className={`bg-white rounded-xl border overflow-hidden ${DOCUMENT_THEME.section.wrap}`}
            >
              {(!surveyorDocumentsView || applicationFormsData.length > 0) && (
              <div className="px-3 pt-3">
                <DocumentBulkActions
                  selectedCount={appSelection.selectedCount}
                  loading={bulkPrintLoading}
                  onPrint={() =>
                    runBulkPrint(
                      appSelection.selectedRows,
                      "form",
                      "Application Forms",
                    )
                  }
                />
              </div>
              )}
              <Table
                columns={documentColumns}
                data={applicationFormsData}
                keyField="_rowKey"
                loading={loadingAppForms}
                density="compact"
                embedded
                tableClassName="table-fixed"
                srNoClassName={DOCUMENT_COL.srNo}
                selectionColClassName={DOCUMENT_COL.lead}
                headTone={DOCUMENT_THEME.tableHead}
                selection={buildTableSelection(appSelection)}
                noDataMessage={documentEmptyMessage}
                pagination={documentPagination(applicationFormsData, {
                  currentPage: appFormsPage,
                  totalPages: totalAppFormsPages,
                  onPageChange: setAppFormsPage,
                  totalItems: appFormsTotal,
                  itemsPerPage: appFormsLimit,
                })}
              />
            </div>
          )}

          {activeTab === "medical-forms" && (
            <div
              className={`bg-white rounded-xl border overflow-hidden ${DOCUMENT_THEME.section.wrap}`}
            >
              {(!surveyorDocumentsView || medicalFormsData.length > 0) && (
              <div className="px-3 pt-3">
                <DocumentBulkActions
                  selectedCount={medicalSelection.selectedCount}
                  loading={bulkPrintLoading}
                  onPrint={() =>
                    runBulkPrint(
                      medicalSelection.selectedRows,
                      "form",
                      "Medical Forms",
                    )
                  }
                />
              </div>
              )}
              <Table
                columns={documentColumns}
                data={medicalFormsData}
                keyField="_rowKey"
                loading={loadingMedicalForms}
                density="compact"
                embedded
                tableClassName="table-fixed"
                srNoClassName={DOCUMENT_COL.srNo}
                selectionColClassName={DOCUMENT_COL.lead}
                headTone={DOCUMENT_THEME.tableHead}
                selection={buildTableSelection(medicalSelection)}
                noDataMessage={documentEmptyMessage}
                pagination={documentPagination(medicalFormsData, {
                  currentPage: medicalFormsPage,
                  totalPages: totalMedicalFormsPages,
                  onPageChange: setMedicalFormsPage,
                  totalItems: medicalFormsTotal,
                  itemsPerPage: medicalFormsLimit,
                })}
              />
            </div>
          )}

          {activeTab === "tax-forms" && (
            <div
              className={`bg-white rounded-xl border overflow-hidden ${DOCUMENT_THEME.section.wrap}`}
            >
              <Table
                columns={taxFormColumns}
                data={taxFormsData}
                loading={loadingTaxForms}
                density="compact"
                embedded
                tableClassName="table-fixed"
                srNoClassName={DOCUMENT_COL.srNo}
                headTone={DOCUMENT_THEME.tableHead}
                noDataMessage={documentEmptyMessage}
                pagination={documentPagination(taxFormsData, {
                  currentPage: taxFormsPage,
                  totalPages: totalTaxFormsPages,
                  onPageChange: setTaxFormsPage,
                  totalItems: taxFormsTotal,
                  itemsPerPage: taxFormsLimit,
                })}
              />
            </div>
          )}

          {activeTab === "evaluation-forms" &&
            visibleDocumentTabs.includes("evaluation-forms") && (
              <div
                className={`bg-white rounded-xl border overflow-hidden ${DOCUMENT_THEME.section.wrap}`}
              >
                {(!surveyorDocumentsView || evaluationFormsData.length > 0) && (
                <div className="px-3 pt-3">
                  <DocumentBulkActions
                    selectedCount={evaluationSelection.selectedCount}
                    loading={bulkPrintLoading}
                    onPrint={() =>
                      runBulkPrint(
                        evaluationSelection.selectedRows,
                        "evaluation",
                        "Evaluation Forms",
                      )
                    }
                  />
                </div>
                )}
                <Table
                  columns={evaluationColumns}
                  data={evaluationFormsData}
                  keyField="_rowKey"
                  loading={loadingEvaluationForms}
                  density="compact"
                  embedded
                  tableClassName="table-fixed"
                  srNoClassName={DOCUMENT_COL.srNo}
                  selectionColClassName={DOCUMENT_COL.lead}
                  headTone={DOCUMENT_THEME.tableHead}
                  selection={buildTableSelection(evaluationSelection)}
                  noDataMessage={documentEmptyMessage}
                  pagination={documentPagination(evaluationFormsData, {
                    currentPage: evaluationFormsPage,
                    totalPages: totalEvaluationFormsPages,
                    onPageChange: setEvaluationFormsPage,
                    totalItems: evaluationFormsTotal,
                    itemsPerPage: evaluationFormsLimit,
                  })}
                />
              </div>
            )}

          {activeTab === "credentials" && (
            <div
              className={`bg-white rounded-xl border overflow-hidden ${DOCUMENT_THEME.section.wrap}`}
            >
              <Table
                columns={credentialColumns}
                data={credentialsData}
                loading={loadingCredentials}
                density="compact"
                embedded
                tableClassName="table-fixed"
                srNoClassName={DOCUMENT_COL.srNo}
                headTone={DOCUMENT_THEME.tableHead}
                noDataMessage={documentEmptyMessage}
                pagination={documentPagination(credentialsData, {
                  currentPage: credentialsPage,
                  totalPages: totalCredentialsPages,
                  onPageChange: setCredentialsPage,
                  totalItems: credentialsTotal,
                  itemsPerPage: credentialsLimit,
                })}
              />
            </div>
          )}

          {activeTab === "service-log" &&
            visibleDocumentTabs.includes("service-log") &&
            selectedUser && (
              <ServiceLog
                employeeId={selectedUser.id}
                tenantId={tenantId}
                isAdmin={isAdmin}
                isEmployeeInactive={isEmployeeInactive}
                employeeName={`${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim()}
                employeeEmail={selectedUser.email || ""}
                companyName={companyName}
                logoUrl={getPrintLogoAbsoluteUrl()}
              />
            )}
        </div>
      </div>

      <CredentialModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveCredential}
        mode={modalMode}
        initialData={currentCredential}
        getCredential={getCredential}
      />

      <TaxFormModal
        isOpen={isTaxFormModalOpen}
        onClose={() => setIsTaxFormModalOpen(false)}
        onSave={handleSaveTaxForm}
        mode={modalMode}
        initialData={currentTaxForm}
        getTaxFormById={getTaxFormById}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete ${{ evaluation: "Evaluation", document: "Document" }[itemToDeleteType] || "Credential Entry"}`}
        message={
          itemToDeleteType === "evaluation"
            ? `Are you sure you want to delete the ${itemToDelete?.type || ""} evaluation for ${itemToDelete?.employee?.firstName || ""} ${itemToDelete?.employee?.lastName || ""}? This action cannot be undone.`
            : itemToDeleteType === "document"
              ? `Are you sure you want to delete "${itemToDelete?.rowName}" document? This action cannot be undone.`
              : `Are you sure you want to delete the latest entry for ${itemToDelete?.rowName} (${itemToDelete?.displayName})? This action cannot be undone.`
        }
        isLoading={isDeleting}
      />

      <Modal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        title={`${activeTab.replace("-", " ").toUpperCase()} - ${currentHistoryRow?.name}`}
        size="xl"
      >
        {currentHistoryRow?.details && currentHistoryRow.details.length > 0 ? (
          <Table
            columns={
              activeTab === "tax-forms" ? taxHistoryColumns : historyColumns
            }
            data={[...(currentHistoryRow.details || [])].sort(
              (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
            )}
            loading={false}
            density="compact"
            tableClassName="table-fixed"
            srNoClassName={DOCUMENT_COL.srNo}
            headTone={DOCUMENT_THEME.tableHead}
          />
        ) : (
          <div className="text-center py-12 bg-indigo-50/40 rounded-xl border border-dashed border-indigo-200">
            <DocumentsIcon className="mx-auto h-12 w-12 text-indigo-300 mb-4" />
            <p className="text-slate-500 font-medium">
              {activeTab === "tax-forms"
                ? "No submission history found for this tax form."
                : "No submission history found for this credential."}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Documents;
